import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { ClubThousand } from '../../../app/package/base/cms/package/cms-domain';
import { ModalType } from '../../../app/package/base/service/modals/modal-service';
import { TrackableEventAction, TrackingEventSource } from '../../../app/package/base/service/tracking/tracking-service';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import { isEmpty } from '../../base/package/util/radash-helper';
import styles from './ui-club-1000-card.css?inline';
import { UiClub1000CardInfoModal } from './ui-club-1000-card-info-modal';

const CName = 'ui-club-1000-card';

@customElement(CName)
export class UIClub1000Card extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: String }) class = '';
  @property({ type: Boolean }) loggedIn = false;
  @property({ type: Object }) clubThousand: Omit<ClubThousand, 'order' | 'show_on_homepage' | 'translations'>;
  @property({ type: Number }) index = 0;

  render() {
    return html`
      <div class="club-1000-card ${this.class}">
        <img
          src=${this.clubThousand.card_image}
          class="card-image"
          crossorigin="anonymous"
          decoding="async"
          fetchpriority="low"
          loading="lazy"
          alt=${this.clubThousand.title}
        />
        <div class="wrapper">
          <div class="text">
            <h2 class="title">${this.clubThousand.title}</h2>
            <h3 class="subtitle">${this.clubThousand.subtitle}</h3>
            <p class="description-wrapper">
              <ui-rich-text class="card-description" .content=${this.clubThousand.card_description}></ui-rich-text>
            </p>
          </div>
          <div class="button-wrapper">
            <ui-btn variant="secondary-outlined" .isBlock=${true} class="card-button" size="custom" @click=${this._openModal}>
              ${window.$app.$t.get(I18n.base.more)}
            </ui-btn>
            ${this.clubThousand.game_id ? this._renderPlayOrLoginButton() : nothing}
          </div>
        </div>
      </div>
    `;
  }

  private _renderPlayOrLoginButton() {
    return this.loggedIn
      ? html`
          <ui-btn
            class="card-button"
            variant="secondary"
            size="custom"
            .isBlock=${true}
            @click=${() => {
              this._trackGameClick();
              this._updateVaixData();
              window.$app.router.navigateToGame(true, this.clubThousand.game_id!);
            }}
          >
            ${window.$app.$t.get(I18n.base.play)}
          </ui-btn>
        `
      : html`
          <ui-btn
            class="card-button"
            variant="secondary"
            size="custom"
            .isBlock=${true}
            @click=${() => {
              this._trackGameClick();
              window.$app.product.gotoLogin(
                TrackingEventSource.CLUB1000X_LOBBY_PREVIEW,
                `/game/real/${this.clubThousand.game_id}`,
              );
            }}
          >
            ${window.$app.$t.get(I18n.base.login)}
          </ui-btn>
        `;
  }

  private _openModal() {
    const modal = new UiClub1000CardInfoModal();
    modal.clubThousand = this.clubThousand;
    modal.loggedIn = this.loggedIn;
    window.$app.modal.show(modal, ModalType.Club1000Info);
  }

  private _trackGameClick() {
    const gameId = this.clubThousand.game_id;
    if (!gameId || isEmpty(gameId)) {
      return;
    }

    window.$app.content
      .getGameInfo(gameId)
      .then(async (gameInfo) => {
        const game = await window.$app.content.getGame(gameId);
        if (game && gameId) {
          const trackingParams = {
            gameCategory: game.labels?.join(' - '),
            gameName: this.clubThousand.title,
            gameProvider: gameInfo.getStudio(),
            gameSource: 'club-1000x',
            gamePosition: undefined,
            gameFilters: undefined,
            winType: undefined,
          };

          window.$app.trackingStore.next({ ...window.$app.trackingStore.value, ...trackingParams });

          window.$app.track.gamePlay({
            eventAction: TrackableEventAction.GAME_SELECTED,
            ...window.$app.trackingStore.value,
          });
        }
      })
      .catch((err) => {
        window.$app.logger.warn(`Error fetching game info '${gameId}'`, err);
      });
  }

  private _updateVaixData() {
    const gameId = this.clubThousand.game_id;
    if (!gameId || isEmpty(gameId)) {
      return;
    }

    const currentLocation = window.$app.router.currentLocation().url;
    window.$app.content.getGame(gameId).then((game) => {
      window.$app.vaix.updateVaixData({
        categorySlug: 'club1000x',
        gameSlug: game.slug,
        page: currentLocation,
        position: this.index,
      });
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIClub1000Card;
  }
}
